import { call, put, takeLatest, select } from 'redux-saga/effects';
import { request } from 'utils/request';
import { headerActions } from './slice';
import { header } from 'utils/auth';
import { selectDealersList } from './selectors';
import { QueryClient } from '@tanstack/react-query';

// Create a query client instance
const queryClient = new QueryClient();

// Query key for dealers
const DEALERS_QUERY_KEY = ['dealers'];

export function* getDealers({ payload }: ReturnType<any>) {
  try {
    yield put(headerActions.setLoading(true));

    // Check if data exists in cache
    const cachedData = queryClient.getQueryData(DEALERS_QUERY_KEY);
    if (cachedData) {
      yield put(headerActions.requestDealersSuccess(cachedData));
      return;
    }

    const requestURL = `/api/v2/dealers`;
    const response = yield call(request, requestURL, {
      method: 'GET',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: header(),
      }),
    });

    if (response.statusCode === 500) {
      yield put(headerActions.requestDealersError(response));
      console.error('Error fetching dealers list');
      return;
    }

    //sorting in alphabetical order
    if (response.body?.items?.length) {
      let responseData = JSON.parse(JSON.stringify(response));

      responseData.body.items = responseData.body.items.map(item => ({
        ...item,
        dealer: item._id || "",
        site_title: item.name || ""
      })).sort((a, b) => {
        if (a.status !== 'ACTIVE' && b.status === 'ACTIVE') {
          return 1;
        } else if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') {
          return -1;
        } else {
          return a.name.toLowerCase() > b.name.toLowerCase()
            ? 1
            : -1;
        }
      });

      // Store in cache
      queryClient.setQueryData(DEALERS_QUERY_KEY, responseData);

      yield put(headerActions.requestDealersSuccess(responseData));
    } else {
      yield put(headerActions.requestDealersError(new Error('No dealers found')));
      console.warn('No dealers found');
    }
  } catch (err: any) {
    console.log(err);
    let errorMessage = 'Error fetching dealers list';

    if (err.response?.status === 404) {
      errorMessage = 'Dealers API endpoint not found';
    } else if (err.message === 'Server Error') {
      errorMessage = 'Server error occurred while fetching dealers';
    }

    yield put(headerActions.requestDealersError(err));
    console.error(errorMessage);
  } finally {
    yield put(headerActions.setLoading(false));
  }
}

export function* setDealer({ payload }: ReturnType<any>) {
  try {
    const dealersState = yield select(selectDealersList);
    const dealers = dealersState.data?.body?.items || [];

    let dealer = dealers?.find(item => item._id === payload.dealerId);

    if (dealer) {
      localStorage.setItem('dealer', JSON.stringify(dealer));
      yield put(headerActions.selectDealer(dealer));
    } else {
      if (payload.history) {
        payload.history.goBack();
        throw new Error('dealer not found');
      }
    }
  } catch (error) {
    console.log(error);
    console.error('Could not select the dealer');
  }
}

export function* headerSaga() {
  yield takeLatest(headerActions.requestDealers.type, getDealers);
  yield takeLatest(headerActions.setDealer.type, setDealer);
}

